/**
 * Accessibility utilities for the PipeEX application
 */

// Generate unique IDs for accessibility purposes
let idCounter = 0;
export const generateId = (prefix: string = 'pipeex'): string => {
  idCounter++;
  return `${prefix}-${idCounter}`;
};

// Focus management utilities
export const focusFirstFocusableElement = (container: HTMLElement | null): void => {
  if (!container) return;
  
  const focusableElements = container.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
  
  if (focusableElements.length > 0) {
    (focusableElements[0] as HTMLElement).focus();
  }
};

export const releaseFocusBeforeOverlay = (
  event?: { currentTarget?: EventTarget | null } | null
): void => {
  const canUseDom =
    typeof document !== 'undefined' &&
    typeof HTMLElement !== 'undefined';

  if (!canUseDom) {
    return;
  }

  const target = event?.currentTarget;
  const activeElement = document.activeElement;

  if (activeElement instanceof HTMLElement) {
    activeElement.blur();
  }

  if (target instanceof HTMLElement) {
    target.blur();
  }
};

// ARIA live region utilities for announcements
export const announceToScreenReader = (message: string): void => {
  const announcement = document.createElement('div');
  const id = `aria-announcement-${Date.now()}`;
  announcement.setAttribute('id', id);
  announcement.setAttribute('aria-live', 'polite');
  announcement.setAttribute('aria-atomic', 'true');
  announcement.className = 'sr-only';
  announcement.textContent = message;
  
  document.body.appendChild(announcement);
  
  // Remove the announcement after a delay
  setTimeout(() => {
    const element = document.getElementById(id);
    if (element) {
      document.body.removeChild(element);
    }
  }, 1000);
};

// Keyboard navigation utilities
export const handleKeyboardNavigation = (
  e: React.KeyboardEvent,
  items: any[],
  currentIndex: number,
  onChange: (index: number) => void
): void => {
  switch (e.key) {
    case 'ArrowDown':
      e.preventDefault();
      onChange((currentIndex + 1) % items.length);
      break;
    case 'ArrowUp':
      e.preventDefault();
      onChange((currentIndex - 1 + items.length) % items.length);
      break;
    case 'Home':
      e.preventDefault();
      onChange(0);
      break;
    case 'End':
      e.preventDefault();
      onChange(items.length - 1);
      break;
    default:
      break;
  }
};

// Focus trap utility for modals and dialogs
export const createFocusTrap = (container: HTMLElement | null): (() => void) => {
  if (!container) {
    return () => {};
  }

  const focusableElements = container.querySelectorAll(
    'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
  );
  
  if (focusableElements.length === 0) {
    return () => {};
  }

  const firstElement = focusableElements[0] as HTMLElement;
  const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

  const handleKeyDown = (e: KeyboardEvent) => {
    if (e.key === 'Tab') {
      if (e.shiftKey) {
        // Shift + Tab
        if (document.activeElement === firstElement) {
          lastElement.focus();
          e.preventDefault();
        }
      } else {
        // Tab
        if (document.activeElement === lastElement) {
          firstElement.focus();
          e.preventDefault();
        }
      }
    }
  };

  document.addEventListener('keydown', handleKeyDown);

  // Focus the first element when trap is created
  firstElement.focus();

  // Return cleanup function
  return () => {
    document.removeEventListener('keydown', handleKeyDown);
  };
};

// Utility to add ARIA attributes to elements
export const addAriaAttributes = (
  element: HTMLElement,
  attributes: { [key: string]: string }
): void => {
  Object.entries(attributes).forEach(([key, value]) => {
    element.setAttribute(key, value);
  });
};

// Utility to remove ARIA attributes from elements
export const removeAriaAttributes = (
  element: HTMLElement,
  attributeNames: string[]
): void => {
  attributeNames.forEach(name => {
    element.removeAttribute(name);
  });
};
